import React, {useEffect, useRef} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {IPresentation} from '../../../../incase-domain/presentation';
import useCreatePresentation from '../../../../service/hooks/useCreatePresentation';
import useImportPptPresentation from '../../../../service/hooks/useImportPptPresentation';
import useImportPresentation from '../../../../service/hooks/useImportPresentation';
import {TEMPLATE} from '../../../../service/template/types';
import PresentationList from '../../../components/presentationList/PresentationList';
import PageHeader from '../../../components/common/header/PageHeader';

function EditorDefaultPage(): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const createPresentationMutation = useCreatePresentation();
    const importPresentationMutation = useImportPresentation();
    const importPptPresentationMutation = useImportPptPresentation();

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!createPresentationMutation.isSuccess || !createPresentationMutation.data) return;

        navigate(`/presentations/${createPresentationMutation.data._id}`, {state: {template: TEMPLATE.TITLE_SUBTITLE}});
        createPresentationMutation.reset();
    }, [createPresentationMutation, navigate]);

    useEffect(() => {
        if (!importPresentationMutation.isSuccess || !importPresentationMutation.data) return;

        navigate(`/presentations/${importPresentationMutation.data._id}`);
        importPresentationMutation.reset();
    }, [importPresentationMutation, navigate]);

    useEffect(() => {
        if (!importPptPresentationMutation.isSuccess || !importPptPresentationMutation.data) return;

        navigate(`/presentations/${importPptPresentationMutation.data._id}`);
        importPptPresentationMutation.reset();
    }, [importPptPresentationMutation, navigate]);

    const onChange = (e: FileList): void => {
        const reader = new FileReader();
        const validExtensions = ['.pptx'];
        const validPresentationExtensions = ['.json', '.nbl'];

        if (validExtensions.some((ext) => e[0].name.endsWith(ext))) {
            importPptPresentationMutation.mutate({file: e[0]});
        } else if (validPresentationExtensions.some((ext) => e[0].name.endsWith(ext))) {
            reader.onload = () => {
                importPresentationMutation.mutate({presentation: reader.result as unknown as IPresentation});
            };
            reader.readAsText(e[0]);
        } else {
            toast.error(t('editorPage.invalidFile'));
        }
    };

    return (
        <>
            <input
                className="u-d-none"
                ref={inputRef}
                type="file"
                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                    const {files} = ev.target;
                    if (files) onChange(files);
                }}
            />
            <PageHeader
                title={t('navigation.presentations')}
                items={[
                    {
                        icon: 'solar:lock-linear',
                        label: t('navigation.personal'),
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        onClick: () => {},
                        active: true,
                    },
                    {
                        icon: 'solar:users-group-rounded-linear',
                        label: t('navigation.shared'),
                        onClick: () => navigate('/presentations/shared'),
                    },
                    {
                        icon: 'solar:posts-carousel-vertical-linear',
                        label: t('homePage.feed'),
                        onClick: () => navigate('/feed'),
                    },
                ]}
                defaultIndex={0}
            />
            <PresentationList
                paginated
                onCreatePresentation={() => createPresentationMutation.mutate(undefined)}
                onImportPresentation={() => {
                    if (inputRef.current) inputRef.current.click();
                }}
            />
        </>
    );
}

export default EditorDefaultPage;
